//standard library imports
import { useEffect, useState } from 'react';
//External library imports
import { get as lodashGet } from 'lodash';
import { toast } from 'react-toastify';
import { UilTrash, UilPen } from '@iconscout/react-unicons';

//Internal module imports
import { useLoader, useQueryParamWithDefault, useUserRole } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import FilterBarClasses from '../../../../styles/Filterbar.module.css';
import { AdminService } from '../../../../services/BiddingService/CommonServices/AdminService';
import { CustomerService } from '../../../../services/BiddingService/CommonServices/CustomerService';
import IconComponent from '../../../../components/Icon/Icon';
import bidClasses from '../Bidding.module.css';
import { StringParam } from 'use-query-params';
import { CreateSymetricalBundle } from './ModalComponent/CreateSymetricalBundle';
import { SymetricalBundleService } from '../../../../services/BiddingService/CommonServices/SymetricalBundleService';
import { formatCustomerName } from '../../../../utils/biddingHelper';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';

const DEFAULT_DROPDOWN_OPTION = {
    label: 'All',
    value: 'all',
};

const getDropdownOption = (data = [], isDefaultOption = true, labelKey = 'name', valueKey = '_id') => {
    const temp = isDefaultOption ? [DEFAULT_DROPDOWN_OPTION] : [];
    data.map((el) => {
        temp.push({ label: lodashGet(el, labelKey), value: lodashGet(el, valueKey) });
    });
    return temp;
};

export const SymetricalBundle = () => {
    const [initialLoad, setInitialLoad] = useState(false);
    const { isRootUser, isAdminUser, adminId } = useUserRole();
    const [startLoader, stopLoader] = useLoader();
    const [admins, setAdmins] = useState([isAdminUser ? {} : DEFAULT_DROPDOWN_OPTION]);
    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, isAdminUser ? adminId : DEFAULT_DROPDOWN_OPTION.value);
    const [customers, setCustomers] = useState([DEFAULT_DROPDOWN_OPTION]);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, DEFAULT_DROPDOWN_OPTION.value);
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });
    const [createEditModal, setCreateEditModal] = useState({ status: false, editBundle: null });
    const [symetricalBundles, setSymetricalBundles] = useState({
        totalCount: 0,
        data: [],
    });
    const [deleteModal, setDeleteModal] = useState({ status: false, id: null });

    useEffect(() => {
        if (isRootUser) getAdmins();
    }, []);

    useEffect(() => {
        if (selectedAdmin && selectedAdmin !== 'all') getCustomers();
        else if (selectedAdmin === 'all') setSelectedCustomer(DEFAULT_DROPDOWN_OPTION.value);
    }, [selectedAdmin]);

    useEffect(() => {
        if (selectedAdmin && selectedCustomer) getSymetricalBundles();
    }, [selectedAdmin, selectedCustomer, paginationQuery.skip]);

    const getAdmins = () => {
        AdminService.GetAdmins(startLoader, handleGetAdminSuccess, handleError, stopLoader);
    };

    const handleGetAdminSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedAdmin) setSelectedAdmin(options[0].value);
        setAdmins(options);
    };

    const getCustomers = () => {
        const params = { adminId: selectedAdmin };
        CustomerService.GetCustomers(params, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedCustomer || initialLoad) setSelectedCustomer(options[0].value);
        setCustomers(options);
    };

    const handlePageChange = (pageNo) => {
        setPaginationQuery({ ...paginationQuery, skip: paginationQuery.limit * pageNo, page: pageNo });
    };

    const getSymetricalBundles = () => {
        const params = {
            adminId: selectedAdmin,
            customerId: selectedCustomer,
            skip: paginationQuery.skip,
            limit: paginationQuery.limit,
        };
        SymetricalBundleService.Get(params, startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ data }) => {
        const processedData = lodashGet(data, ['data', 'bundles'], []).map((el) => ({
            name: el.name,
            admin: lodashGet(el, ['admin', 0, 'name']),
            customer: formatCustomerName(lodashGet(el, ['instanceBundle'], []), 'customerName'),
            market: el.market,
            insBundle: formatCustomerName(lodashGet(el, ['instanceBundle'], [])),
            type: el?.type || '--',
            action: (
                <div className={bidClasses.ActionContainer}>
                    <div onClick={() => setCreateEditModal({ status: true, editBundle: el })}>
                        <IconComponent icon={UilPen} tooltip={'Edit'} />
                    </div>
                    <div onClick={() => setDeleteModal({ status: true, id: el._id })}>
                        <IconComponent icon={UilTrash} tooltip={'Delete'} />
                    </div>
                </div>
            ),
        }));
        setSymetricalBundles({
            totalCount: lodashGet(data, ['data', 'count', 0, 'count'], 0),
            data: processedData,
        });
    };

    const handleDelete = (id) => {
        SymetricalBundleService.Delete(id, startLoader, deleteSuccess, handleError, stopLoader);
    };

    const deleteSuccess = () => {
        toast.success('Linked Bundle deleted successfully!');
        getSymetricalBundles();
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    return (
        <div>
            <div className={FilterBarClasses.ParentWrapper} style={{ alignItems: 'center' }}>
                <div className={FilterBarClasses.Left}>
                    {isRootUser && (
                        <div className={FilterBarClasses.Filter}>
                            <label>Admin</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent
                                    defaultValue={{ value: selectedAdmin }}
                                    options={admins}
                                    onChange={(e) => {
                                        setSelectedAdmin(e.value);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={FilterBarClasses.Filter}>
                        <label>Customer</label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={{ value: selectedCustomer }}
                                options={customers}
                                onChange={(e) => {
                                    setSelectedCustomer(e.value);
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={FilterBarClasses.Right} style={{ alignItems: 'center' }}>
                    <button className="btn-primary" style={{ width: 'auto' }} onClick={() => getSymetricalBundles()}>
                        Submit
                    </button>

                    <button className="btn-primary" style={{ width: 'auto' }} onClick={() => setCreateEditModal({ status: true, editBundle: null })}>
                        Create
                    </button>
                </div>
            </div>

            <div className={bidClasses.MainContainer}>
                <div className={bidClasses.TableHeader}>
                    <div>
                        <Typography content={'Linked Bundles'} />
                        <div className={bidClasses.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={symetricalBundles.totalCount} />
                            </span>
                        </div>
                    </div>
                </div>
                <Table
                    head={['Name', 'Admin', 'Customer', 'Type', 'Instance Bundle', 'Market', 'Action']}
                    keys={['name', 'admin', 'customer', 'type', 'insBundle', 'market', 'action']}
                    data={symetricalBundles.data}
                    page={paginationQuery.page}
                    Pagination={true}
                    limit={paginationQuery.limit}
                    handlePageChange={handlePageChange}
                    totalCount={symetricalBundles.totalCount}
                />
            </div>
            {createEditModal.status && (
                <ModalComponent isOpen={createEditModal.status} setOpen={() => setCreateEditModal({ status: false, editBundle: null })} style={{ overflow: 'initial' }}>
                    {createEditModal.status && (
                        <CreateSymetricalBundle admins={admins} editBundle={createEditModal.editBundle} setOpen={setCreateEditModal} getSymetricalBundles={getSymetricalBundles} />
                    )}
                </ModalComponent>
            )}
            <DeleteModal
                deletefunction={handleDelete}
                opendeleteModal={deleteModal.status}
                setOpenDeleteModal={(status) => setDeleteModal({ id: null, status })}
                deviceId={deleteModal.id}
            ></DeleteModal>
        </div>
    );
};
